export type ExpressionTree = ExpressionNode[];
export type ExpressionNode =
  | {
      kind: 'number';
      value: number;
    }
  | {
      kind: 'operator';
      type: '+' | '-' | '*' | '/' | '%';
    }
  | {
      kind: 'field';
      //type: string;
      value: string;
    }
  | {
      kind: 'group';
      type: 'parenthesis';
      value: ExpressionTree;
      functionArgument?: boolean;
    }
  | {
      kind: 'func';
      type: string;
      value: ExpressionTree;
    };

export type ExpressionFn = (a: number, b: number) => number;

export const expression = {
  toString(value: ExpressionTree, separator = ' '): string {
    return value
      .map((expr) => {
        switch (expr.kind) {
          case 'number':
            return expr.value;
          case 'operator':
            return expr.type;
          case 'field':
            return `"${expr.value}"`;
          case 'func':
            return `${expr.type}(${this.toString(expr.value, ', ')})`;
          case 'group':
            return isArgumentGroup(expr)
              ? this.toString(expr.value, ' ')
              : `(${this.toString(expr.value, ' ')})`;
          default:
            return '';
        }
      })
      .join(separator);
  },
  toTree(value: string): ExpressionTree {
    function parseValue(
      expression: string,
      initialPosition = 0,
      parentNode?: ExpressionNode
    ): { tree: ExpressionTree; returnPosition: number } {
      let position = initialPosition;
      let skip = false;
      const tree: ExpressionTree = [];
      let argumentStart = 0;
      const finishArgument = () => {
        if (tree.length === argumentStart)
          throw new Error('Expected a function argument');
        if (tree.length - argumentStart > 1) {
          const argument = tree.splice(argumentStart);
          // Preserve argument boundaries without adding source nesting.
          const argumentNode: ExpressionNode = {
            kind: 'group',
            type: 'parenthesis',
            value: argument,
            functionArgument: true,
          };
          tree.push(argumentNode);
        }
        argumentStart = tree.length;
      };

      // Regex
      const numRegex = /[0-9.]/;
      const letterRegex = /[a-zA-Z]/;
      const operatorRegex = new RegExp(
        `[${Object.keys(defaultOperations)
          .map((c) => `\\${c}`)
          .join('')}]`
      );

      const checkPreviousNode = (
        previousNode: ExpressionNode | undefined,
        allowedTypes: ExpressionNode['kind'][]
      ) => {
        if (previousNode && !allowedTypes.includes(previousNode.kind)) {
          throw new Error(
            `Incorrect previous node of type ${previousNode.kind}.`
          );
        }
      };

      const arr = expression.split('');
      for (let i = 0; i < arr.length; i++) {
        const previousNode =
          tree.length > argumentStart ? tree.at(-1) : undefined;
        const char = arr[i];

        if (i < position || skip) {
          continue;
        }

        // Accounts
        if (char === '"') {
          checkPreviousNode(previousNode, [
            'func',
            'group',
            'operator',
            'number',
          ]);

          let temp = '';
          let nextPos = 1;

          while (i + nextPos < arr.length && arr[i + nextPos] !== '"') {
            temp = `${temp}${arr[i + nextPos]}`;
            nextPos++;
          }

          if (i + nextPos >= arr.length)
            throw new Error('Unclosed expression field');

          const node: ExpressionNode = {
            kind: 'field',
            //type: temp,
            value: temp,
          };
          tree.push(node);
          position = i + nextPos + 1;
          continue;
        }

        // Numbers
        if (numRegex.test(char)) {
          checkPreviousNode(
            previousNode,
            parentNode?.kind === 'func'
              ? ['operator', 'number', 'field', 'group', 'func']
              : ['operator']
          );
          let temp = char;
          let nextPos = 1;
          while (numRegex.test(arr[i + nextPos])) {
            temp = `${temp}${arr[i + nextPos]}`;
            nextPos++;
          }
          const node: ExpressionNode = {
            kind: 'number',
            value: Number.parseFloat(temp),
          };
          tree.push(node);
          position = i + nextPos;
          continue;
        }

        // Operators
        if (operatorRegex.test(char)) {
          checkPreviousNode(previousNode, [
            'func',
            'group',
            'field',
            'number',
            'operator',
          ]);

          if (
            previousNode?.kind === 'operator' &&
            ['*', '/', '%'].includes(char)
          ) {
            throw new Error('Incorrect operator chain');
          }

          const node: ExpressionNode = {
            kind: 'operator',
            type: char as '+' | '-' | '*' | '/' | '%',
          };
          tree.push(node);
          continue;
        }

        // Paranthesis
        if (char === '(') {
          checkPreviousNode(previousNode, ['operator']);
          // returns tree and position to continue from
          const { tree: childTree, returnPosition } = parseValue(
            expression,
            i + 1,
            { kind: 'group', value: [], type: 'parenthesis' }
          );
          const node: ExpressionNode = {
            kind: 'group',
            type: 'parenthesis',
            value: childTree,
          };
          tree.push(node);
          position = returnPosition;
          continue;
        }

        // Functions
        if (letterRegex.test(char)) {
          checkPreviousNode(previousNode, ['operator']);
          let temp = char;
          let nextPos = 1;

          while (arr[i + nextPos] && letterRegex.test(arr[i + nextPos])) {
            temp = `${temp}${arr[i + nextPos]}`;
            nextPos++;
          }

          const func = temp.toLowerCase();

          if (!(func in defaultFunctions)) {
            throw Error(`Function '${temp}' does not exist.`);
          }
          const { tree: childTree, returnPosition } = parseValue(
            expression,
            i + nextPos + 1,
            { kind: 'func', value: [], type: func }
          );

          const node: ExpressionNode = {
            kind: 'func',
            type: func,
            value: childTree,
          };

          tree.push(node);
          position = returnPosition;

          continue;
        }

        if (char === ',') {
          if (parentNode?.kind !== 'func') throw new Error('Unexpected comma');
          finishArgument();
          continue;
        }

        // Skip from now and return tree and position up to here
        if (char === ')') {
          if (parentNode?.kind === 'func') finishArgument();
          skip = true;
          position = i + 1;
        }
      }
      return { tree, returnPosition: position };
    }

    const { tree } = parseValue(value);

    return tree;
  },
  execute(
    value: ExpressionTree,
    context: {
      functions?: Record<string, ExpressionFn>;
      getData?: (field: string) => number | undefined;
      explain?: boolean;
    } = {}
  ): number {
    const functions = { ...defaultFunctions, ...context.functions };
    const getData = context.getData || (() => undefined);
    const explain: string[] = [];
    function evaluateTree(
      tree: ExpressionTree,
      parent?: ExpressionNode,
      level = 0
    ): number {
      assertDepth(level);
      const resolved = tree.map((node): ResolvedNode => {
        if (node.kind === 'group' || node.kind === 'func') {
          return evaluateTree(
            node.value,
            node,
            level + (parent?.kind === 'func' && isArgumentGroup(node) ? 0 : 1)
          );
        }
        if (node.kind === 'number') return node.value;
        if (node.kind === 'operator') return node;
        const data = getData(node.value);
        if (data === undefined) throw new Error(`No data for ${node.value}`);
        return data;
      });
      const result = evaluateResolvedTree(resolved, parent, functions);
      if (context.explain) explain.push(`Eval level ${level}: ${result}`);
      return result;
    }
    const result = evaluateTree(value);
    if (context.explain) console.log(explain);
    return result;
  },
  async executeAsync(
    value: ExpressionTree,
    context: {
      functions?: Record<string, ExpressionFn>;
      getData?: (
        field: string
      ) => Promise<number | undefined> | number | undefined;
    } = {}
  ): Promise<number> {
    const functions = { ...defaultFunctions, ...context.functions };
    const getData = context.getData || (() => undefined);
    async function evaluateTree(
      tree: ExpressionTree,
      parent?: ExpressionNode,
      level = 0
    ): Promise<number> {
      assertDepth(level);
      const resolved: ResolvedNode[] = [];
      for (const node of tree) {
        if (node.kind === 'group' || node.kind === 'func') {
          resolved.push(
            await evaluateTree(
              node.value,
              node,
              level + (parent?.kind === 'func' && isArgumentGroup(node) ? 0 : 1)
            )
          );
        } else if (node.kind === 'number') {
          resolved.push(node.value);
        } else if (node.kind === 'operator') {
          resolved.push(node);
        } else {
          const data = await getData(node.value);
          if (data === undefined) throw new Error(`No data for ${node.value}`);
          resolved.push(data);
        }
      }
      return evaluateResolvedTree(resolved, parent, functions);
    }
    return await evaluateTree(value);
  },
};

type ResolvedNode = number | Extract<ExpressionNode, { kind: 'operator' }>;

function isArgumentGroup(node: ExpressionNode) {
  return (
    node.kind === 'group' &&
    'functionArgument' in node &&
    node.functionArgument === true
  );
}

function assertDepth(level: number) {
  if (level > 10) throw new Error(`Max recursion depth reached (${level})`);
}

function evaluateResolvedTree(
  nodes: readonly ResolvedNode[],
  parent: ExpressionNode | undefined,
  functions: Record<string, ExpressionFn>
) {
  if (!nodes.length) return 0;
  let position = 0;
  const operator = () => {
    const node = nodes[position];
    return typeof node === 'object' ? node.type : undefined;
  };
  const readOperand = () => {
    let sign = 1;
    for (; operator() === '+' || operator() === '-'; position++) {
      if (operator() === '-') sign *= -1;
    }
    const node = nodes[position++];
    if (typeof node !== 'number')
      throw new Error('Expected an expression operand');
    return sign * node;
  };
  const readProduct = () => {
    let result = readOperand();
    for (
      let type = operator();
      type === '*' || type === '/' || type === '%';
      type = operator()
    ) {
      position++;
      result = defaultOperations[type](result, readOperand());
    }
    return result;
  };
  const readSum = () => {
    let result = readProduct();
    for (
      let type = operator();
      type === '+' || type === '-';
      type = operator()
    ) {
      position++;
      result = defaultOperations[type](result, readProduct());
    }
    return result;
  };
  let result = readSum();
  for (; position < nodes.length; ) {
    if (parent?.kind !== 'func')
      throw new Error('Expected an expression operator');
    const fn =
      functions[parent.type.toLowerCase()] ||
      functions[parent.type.toUpperCase()];
    if (!fn) throw new Error(`No function called ${parent.type} found`);
    result = fn(result, readSum());
  }
  return result;
}

const basicOperations = {
  '+': (a: number, b: number) => a + b,
  '-': (a: number, b: number) => a - b,
};
const multiplicationOperations = {
  '/': (a: number, b: number) => a / b,
  '*': (a: number, b: number) => a * b,
};
const nonBasicOperations = {
  '%': (a: number, b: number) => (a / 100) * b,
};
const defaultOperations = {
  ...basicOperations,
  ...multiplicationOperations,
  ...nonBasicOperations,
};

const defaultFunctions = {
  sum: (a: number, b: number) => a + b,
  min: (a: number, b: number) => Math.min(a, b),
  max: (a: number, b: number) => Math.max(a, b),
};
